/**
 * Línea de tiempo del cliente: une movimientos, mails y auditoría en una sola lista, la más nueva primero.
 *
 * Los mails y la auditoría ordenan por su `created_at`. Un movimiento ordena por su `fecha` a medianoche
 * local, así el aviso o el recibo de un día quedan arriba de la deuda o el pago de ese mismo día.
 */
import type { EmailLogEntry } from '@/services/emailLogService'
import type { Movimiento } from '@/types/database'
import { ETIQUETA_ETAPA } from '@/emails/templates/reporteCobranza.copy'

/** Las columnas de `movimientos` que usa la línea de tiempo. */
export type MovimientoLinea = Pick<
  Movimiento,
  'id' | 'tipo' | 'concepto' | 'fecha' | 'debe' | 'haber' | 'mes_anio' | 'created_at' | 'created_by'
>

/** Una fila `cliente/crear` o `cliente/editar` de `audit_log`, con el estado que guardó la edición. */
export interface FilaEstadoCliente {
  id: string
  accion: string
  created_at: string | null
  user_email: string
  estado: string | null
}

/** Una fila `movimiento/eliminar` de `audit_log`, con la fecha del `movimiento/crear` del mismo movimiento. */
export interface FilaMovimientoEliminado {
  id: string
  created_at: string | null
  user_email: string
  detalle: unknown
  registrado_el: string | null
}

export type TipoEventoCliente = 'alta' | 'baja' | 'reactivacion' | 'suspension'

interface EventoBase {
  id: string
  /** Milisegundos por los que se ordena la lista. */
  orden: number
  /** Desempate entre eventos con el mismo `orden`. */
  desempate: number
  fecha: string
}

export interface EventoMovimiento extends EventoBase {
  tipo: 'deuda' | 'pago'
  concepto: string
  monto: number
  periodo: string | null
  /** El `created_at` del movimiento, solo cuando cae otro día que su `fecha`. */
  cargadoEl: string | null
  cargadoPor: string | null
}

export interface EventoEliminado extends EventoBase {
  tipo: 'eliminado'
  tipoMovimiento: string
  concepto: string
  monto: number
  borradoPor: string
  fechaMovimiento: string | null
  cargadoEl: string | null
}

export interface EventoMail extends EventoBase {
  tipo: 'mail'
  mail: EmailLogEntry
}

export interface EventoCliente extends EventoBase {
  tipo: TipoEventoCliente
  usuario: string
}

export type EventoLinea = EventoMovimiento | EventoEliminado | EventoMail | EventoCliente

export const TITULO_EVENTO_CLIENTE: Record<TipoEventoCliente, string> = {
  alta: 'Alta del cliente',
  baja: 'Baja del cliente',
  reactivacion: 'Reactivación del cliente',
  suspension: 'Suspensión del cliente',
}

const ETIQUETA_ETAPA_AVISO: Record<string, string> = { ...ETIQUETA_ETAPA, alta: 'Aviso de alta con deuda' }

/** Nombre en español de la etapa de un aviso de cobranza. Una etapa sin entrada se muestra cruda. */
export const etiquetaEtapaAviso = (etapa: string): string => ETIQUETA_ETAPA_AVISO[etapa] ?? etapa

const ETIQUETA_CANAL: Record<string, string> = {
  whatsapp: 'WhatsApp',
  telefono: 'Teléfono',
  persona: 'En persona',
  otro: 'Otro',
}

/** Nombre en español del canal de un envío registrado a mano. Un canal sin entrada se muestra crudo. */
export const etiquetaCanal = (canal: string): string => ETIQUETA_CANAL[canal] ?? canal

const EVENTO_POR_ESTADO: Record<string, TipoEventoCliente> = {
  inactivo: 'baja',
  activo: 'reactivacion',
  suspendido: 'suspension',
}

const inicioDelDia = (fecha: string): number => new Date(`${fecha.slice(0, 10)}T00:00:00`).getTime()

const instante = (timestamp: string | null): number => (timestamp ? new Date(timestamp).getTime() : 0)

const diaLocal = (timestamp: string): string => {
  const d = new Date(timestamp)
  const dosDigitos = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${dosDigitos(d.getMonth() + 1)}-${dosDigitos(d.getDate())}`
}

const texto = (valor: unknown): string | null => (typeof valor === 'string' && valor ? valor : null)

const numero = (valor: unknown): number => (typeof valor === 'number' ? valor : Number(valor) || 0)

export const eventoDeMovimiento = (m: MovimientoLinea): EventoMovimiento => {
  const esPago = m.tipo === 'Pago'
  const cargadoOtroDia = !!m.created_at && diaLocal(m.created_at) !== m.fecha.slice(0, 10)
  return {
    id: `mov:${m.id}`,
    tipo: esPago ? 'pago' : 'deuda',
    orden: inicioDelDia(m.fecha),
    desempate: instante(m.created_at),
    fecha: m.fecha,
    concepto: m.concepto,
    monto: esPago ? numero(m.haber) : numero(m.debe),
    periodo: m.mes_anio,
    cargadoEl: cargadoOtroDia ? m.created_at : null,
    cargadoPor: m.created_by,
  }
}

export const eventoDeEliminado = (fila: FilaMovimientoEliminado): EventoEliminado => {
  const detalle = (fila.detalle ?? {}) as Record<string, unknown>
  const tipoMovimiento = texto(detalle.tipo) ?? 'Movimiento'
  return {
    id: `audit:${fila.id}`,
    tipo: 'eliminado',
    orden: instante(fila.created_at),
    desempate: 0,
    fecha: fila.created_at ?? '',
    tipoMovimiento,
    concepto: texto(detalle.concepto) ?? '',
    monto: tipoMovimiento === 'Pago' ? numero(detalle.haber) : numero(detalle.debe),
    borradoPor: fila.user_email,
    fechaMovimiento: texto(detalle.fecha),
    cargadoEl: fila.registrado_el,
  }
}

export const eventoDeMail = (mail: EmailLogEntry): EventoMail => ({
  id: `mail:${mail.id}`,
  tipo: 'mail',
  orden: instante(mail.created_at),
  desempate: 0,
  fecha: mail.created_at,
  mail,
})

/**
 * Convierte las filas de auditoría del cliente, ordenadas de la más vieja a la más nueva, en eventos.
 *
 * El formulario de edición manda el estado en cada guardado, así que una edición solo es evento cuando
 * su estado difiere del anterior. El estado de partida es `activo`, el default de `clientes.estado`.
 */
export const eventosDeEstado = (filas: FilaEstadoCliente[]): EventoCliente[] => {
  let anterior = 'activo'
  const eventos: EventoCliente[] = []
  for (const fila of filas) {
    const base = {
      id: `audit:${fila.id}`,
      orden: instante(fila.created_at),
      desempate: 0,
      fecha: fila.created_at ?? '',
      usuario: fila.user_email,
    }
    if (fila.accion === 'crear') {
      eventos.push({ ...base, tipo: 'alta' })
      continue
    }
    const estado = fila.estado?.toLowerCase()
    if (!estado || estado === anterior) continue
    const tipo = EVENTO_POR_ESTADO[estado]
    if (tipo) eventos.push({ ...base, tipo })
    anterior = estado
  }
  return eventos
}

export interface FuentesLineaDeTiempo {
  movimientos: MovimientoLinea[]
  mails: EmailLogEntry[]
  estados: FilaEstadoCliente[]
  eliminados: FilaMovimientoEliminado[]
}

/** Une las cuatro fuentes y las ordena, la más nueva primero. Solo cuenta movimientos `Deuda` y `Pago`. */
export const armarLineaDeTiempo = ({ movimientos, mails, estados, eliminados }: FuentesLineaDeTiempo): EventoLinea[] =>
  [
    ...movimientos.filter((m) => m.tipo === 'Deuda' || m.tipo === 'Pago').map(eventoDeMovimiento),
    ...eliminados.map(eventoDeEliminado),
    ...mails.map(eventoDeMail),
    ...eventosDeEstado(estados),
  ].sort((a, b) => b.orden - a.orden || b.desempate - a.desempate)
